import { getCareSourceOriginalText } from '../careSourceProcessing/careSourceStatusModel';
import { normalizePolicyCategory } from '../careSourceProcessing/careKnowledgeDurabilityPolicy';
import { normalizeAppointmentTime } from './appointmentTime';
import { parseAppointmentStatement } from './appointmentStatementParser';

const APPOINTMENT_TEXT_PATTERN = /\b(?:appointment|appt\.?|doctor(?:'s)? visit|dr\.?\s+(?:appointment|appt|visit)|follow[- ]?up visit)\b/i;
const FUTURE_TEXT_PATTERN = /\b(?:tomorrow|next\s+(?:week|month|year|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|upcoming|scheduled|rescheduled|will\s+(?:see|visit|meet)|(?:have|has|having)\s+(?:an?\s+)?(?:appointment|appt\.?|doctor(?:'s)? visit|dr\.?\s+(?:appointment|appt|visit)))\b/i;
const PAST_TEXT_PATTERN = /\b(?:yesterday|last\s+(?:week|month|year|monday|tuesday|wednesday|thursday|friday|saturday|sunday)|went\s+to|had\s+(?:an?\s+)?(?:appointment|appt\.?|doctor(?:'s)? visit))\b/i;

const getStructuredValue = (suggestion = {}, keys = []) => {
  for (const key of keys) {
    const value = suggestion[key] ?? suggestion.metadata?.[key];
    if (String(value || '').trim()) return String(value).trim();
  }
  return '';
};

const getZonedNowKeys = (now, timeZone) => {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now).reduce((values, part) => ({ ...values, [part.type]: part.value }), {});
    return { dayKey: `${parts.year}-${parts.month}-${parts.day}`, timeKey: `${parts.hour}:${parts.minute}` };
  } catch {
    return null;
  }
};

export const buildAppointmentDraftFromCareSources = (sources = [], now = new Date(), timeZone = '') => {
  for (const source of sources) {
    const suggestions = Array.isArray(source?.suggestedCareKnowledgeItems)
      ? source.suggestedCareKnowledgeItems
      : [];
    const suggestion = suggestions.find((item) => {
      const declaredType = String(item?.knowledgeType || item?.factType || item?.bucket || '').trim().toLowerCase();
      return ['appointment', 'appointments', 'visit', 'follow_up', 'followup'].includes(declaredType)
        || normalizePolicyCategory(item) === 'appointment';
    });
    const originalText = getCareSourceOriginalText(source);
    if (!suggestion && !APPOINTMENT_TEXT_PATTERN.test(originalText)) continue;

    const combinedText = [suggestion?.title, suggestion?.content, originalText].filter(Boolean).join(' ');
    const parsedStatement = parseAppointmentStatement(originalText, { now, timeZone });
    const title = String(suggestion?.title || '').trim()
      || parsedStatement.title
      || (/\b(?:doctor|dr\.?)\b/i.test(combinedText) ? 'Doctor appointment' : 'Appointment');
    const date = getStructuredValue(suggestion, ['localDate', 'startDate', 'date']) || parsedStatement.date;
    const time = normalizeAppointmentTime(getStructuredValue(suggestion, ['localTime', 'startTime', 'time'])) || parsedStatement.time;
    const provider = getStructuredValue(suggestion, ['provider', 'providerName']) || parsedStatement.provider;
    const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
    const zonedNow = getZonedNowKeys(now, timeZone);
    const hasFutureEvidence = validDate && zonedNow
      ? validDate > zonedNow.dayKey || (validDate === zonedNow.dayKey && Boolean(time) && time > zonedNow.timeKey)
      : FUTURE_TEXT_PATTERN.test(combinedText);
    if (!hasFutureEvidence || (!validDate && PAST_TEXT_PATTERN.test(combinedText))) continue;

    return {
      title, provider, date: validDate, time,
      sourceRef: { sourceId: source.id, sourceType: 'care_source' },
      ...(source.appointmentScheduleRef?.appointmentId ? { id: source.appointmentScheduleRef.appointmentId } : {}),
      ...(originalText ? { originalStatement: originalText } : {}),
      ...(parsedStatement.clarification ? { clarification: parsedStatement.clarification } : {}),
    };
  }
  return null;
};
